/**
 * Utilities for formatting and extracting lead names and initials
 */

/**
 * Checks whether a given name value is missing, empty, or a generic placeholder.
 * @param {any} name 
 * @returns {boolean}
 */
export const isUnknownName = (name) => {
  if (name === null || name === undefined) return true;
  if (typeof name !== 'string') return true;
  const trimmed = name.trim().toLowerCase();
  if (!trimmed) return true;

  const unknownValues = ['unknown', 'unknown lead', 'unknow lead', 'unknown user', 'unknown prompt', 'unknown name', 'null', 'undefined', 'none', 'n/a', 'na', '—', '-', '?'];
  return unknownValues.includes(trimmed);
};

/**
 * Extracts and formats a clean human-readable name from an email address or username.
 * Examples:
 *   "john.doe@gmail.com"           -> "John Doe"
 *   "raveen@gmail.com"             -> "Raveen"
 *   "deekshitha.test_intent@gmail" -> "Deekshitha Test Intent"
 *   "alex_smith-jones@corp.com"    -> "Alex Smith Jones"
 * 
 * @param {string} email 
 * @returns {string}
 */
export const extractNameFromEmail = (email) => {
  if (!email || typeof email !== 'string') return '';
  const trimmed = email.trim();
  if (!trimmed) return '';

  // Extract username before '@'
  let username = trimmed.includes('@') ? trimmed.split('@')[0] : trimmed;

  // Strip email alias/tags if present (e.g., "user+test")
  if (username.includes('+')) {
    username = username.split('+')[0];
  }
  if (!username) return '';

  // Split on delimiters like dots, underscores, dashes
  const parts = username.split(/[._-]+/).filter(Boolean);
  if (parts.length === 0) return username;

  const formattedParts = parts.map((part) => {
    // If part is all uppercase and longer than 1 char, normalize to lowercase first
    const isAllUpper = part === part.toUpperCase() && part.length > 1;
    const lower = isAllUpper ? part.toLowerCase() : part;
    return lower.charAt(0).toUpperCase() + lower.slice(1);
  });

  return formattedParts.join(' ').trim();
};

/**
 * Resolves the display lead name.
 * If name is missing, empty, or "UNKNOWN", extracts the name from email.
 * If name itself is an email address (contains '@'), formats it into a proper name.
 * 
 * @param {string} name 
 * @param {string} email 
 * @param {string} fallback 
 * @returns {string}
 */
export const getLeadDisplayName = (name, email, fallback = '') => {
  // If name itself looks like an email address, format from it
  if (name && typeof name === 'string' && name.includes('@')) {
    const fromNameEmail = extractNameFromEmail(name);
    if (fromNameEmail && !isUnknownName(fromNameEmail)) {
      return fromNameEmail;
    }
  }

  // If a valid, non-placeholder name is provided
  if (!isUnknownName(name)) {
    return String(name).trim();
  }

  // If name is unknown or empty, extract from email
  if (email && typeof email === 'string') {
    const fromEmail = extractNameFromEmail(email);
    if (fromEmail && !isUnknownName(fromEmail)) {
      return fromEmail;
    }
  }

  if (fallback) return fallback;
  if (email && typeof email === 'string' && email.includes('@')) {
    return email.split('@')[0];
  }
  return 'Lead';
};

/**
 * Computes initials for avatar display from the resolved display name.
 * 
 * @param {string} name 
 * @param {string} email 
 * @returns {string}
 */
export const getLeadInitials = (name, email) => {
  const displayName = getLeadDisplayName(name, email);
  if (!displayName || isUnknownName(displayName)) return '??';

  const parts = displayName.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return displayName.substring(0, 2).toUpperCase();
};
